// Completely rewritten menu system with isolated submenu architecture
class SachragaMenuFixed {
  constructor() {
    this.menuDrawer = null;
    this.menuOverlay = null;
    this.closeButton = null;
    this.hamburgerButton = null;
    this.activeSubmenu = null;
    this.isOpen = false;
    this.isMobile = window.innerWidth < 990;
    this.welcomeMessage = null;
    this.submenuContainer = null; // Separate container for all submenus
    this.menuButtons = new Map(); // Map menu buttons to their submenus
    
    this.init();
  }
  
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    document.body.classList.add('sachraga-menu-enabled');
    
    if (this.isMobile) {
      this.hamburgerButton = document.querySelector('.header__icon--menu');
      if (!this.hamburgerButton) return;
      
      this.createMenuStructure();
      this.bindEvents();
    }
  }
  
  createMenuStructure() {
    const menuData = this.getMenuData();
    if (!menuData) return;
    
    // Create overlay
    this.menuOverlay = document.createElement('div');
    this.menuOverlay.className = 'sachraga-menu-overlay';
    document.body.appendChild(this.menuOverlay);
    
    // Create menu drawer
    this.menuDrawer = document.createElement('div');
    this.menuDrawer.className = 'sachraga-menu-drawer';
    
    // Create close button
    this.closeButton = document.createElement('button');
    this.closeButton.className = 'sachraga-menu-close';
    this.closeButton.setAttribute('aria-label', 'Close menu');
    this.closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;
    
    // Create menu content
    const menuContent = document.createElement('div');
    menuContent.className = 'sachraga-menu-content';
    
    // Create main navigation (ONLY main menu buttons, NO submenus)
    const menuNav = document.createElement('nav');
    menuNav.className = 'sachraga-menu-nav';
    this.menuNav = menuNav;
    
    const menuList = document.createElement('ul');
    menuList.className = 'sachraga-menu-list';
    
    // Create separate submenu container (completely separate from main menu)
    this.submenuContainer = document.createElement('div');
    this.submenuContainer.className = 'sachraga-submenu-container';
    this.submenuContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `;
    
    // Build main menu items (buttons only)
    menuData.forEach((item, index) => {
      const menuItem = this.createMainMenuItem(item, index);
      menuList.appendChild(menuItem);
      
      // If item has submenu, create it in separate container
      if (item.links && item.links.length > 0) {
        const submenu = this.createSeparateSubmenu(item, index);
        this.submenuContainer.appendChild(submenu);
      }
    });
    
    menuNav.appendChild(menuList);
    menuContent.appendChild(menuNav);
    
    // Add welcome message for desktop
    if (!this.isMobile) {
      this.welcomeMessage = document.createElement('div');
      this.welcomeMessage.className = 'sachraga-menu-welcome';
      this.welcomeMessage.innerHTML = '<h2>Select a category</h2>';
      menuContent.appendChild(this.welcomeMessage);
    }
    
    // Assemble everything
    this.menuDrawer.appendChild(this.closeButton);
    this.menuDrawer.appendChild(menuContent);
    this.menuDrawer.appendChild(this.submenuContainer); // Separate submenu container
    
    document.body.appendChild(this.menuDrawer);
  }
  
  createMainMenuItem(item, index) {
    const li = document.createElement('li');
    li.className = 'sachraga-menu-item';
    
    if (item.links && item.links.length > 0) {
      // Has submenu - create button only
      const button = document.createElement('button');
      button.className = 'sachraga-menu-link';
      button.dataset.menuIndex = index;
      button.dataset.menuTitle = item.title;
      button.innerHTML = `
        <span>${item.title}</span>
        <span class="sachraga-menu-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      `;
      
      // Store button reference for submenu mapping
      this.menuButtons.set(index, button);
      
      li.appendChild(button);
    } else {
      // Regular link
      const link = document.createElement('a');
      link.className = 'sachraga-menu-link';
      link.href = item.url || '#';
      link.innerHTML = `<span>${item.title}</span>`;
      
      li.appendChild(link);
    }
    
    return li;
  }
  
  createSeparateSubmenu(parentItem, index) {
    const submenu = document.createElement('div');
    submenu.className = 'sachraga-submenu';
    submenu.dataset.menuIndex = index;
    submenu.dataset.parentTitle = parentItem.title;
    submenu.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgb(var(--color-background));
      transform: translateX(100%);
      transition: transform 0.3s ease;
      pointer-events: auto;
    `;
    
    // Create header
    const header = document.createElement('div');
    header.className = 'sachraga-submenu-header';
    
    const backButton = document.createElement('button');
    backButton.className = 'sachraga-submenu-back';
    backButton.dataset.menuIndex = index;
    backButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    `;
    
    const title = document.createElement('h3');
    title.className = 'sachraga-submenu-title';
    title.textContent = parentItem.title;
    
    header.appendChild(backButton);
    header.appendChild(title);
    
    // Create submenu list
    const submenuList = document.createElement('ul');
    submenuList.className = 'sachraga-submenu-list';
    
    parentItem.links.forEach(link => {
      const li = document.createElement('li');
      li.className = 'sachraga-submenu-item';
      
      const a = document.createElement('a');
      a.className = 'sachraga-submenu-link';
      a.href = link.url || '#';
      a.textContent = link.title;
      
      li.appendChild(a);
      submenuList.appendChild(li);
    });
    
    submenu.appendChild(header);
    submenu.appendChild(submenuList);
    
    return submenu;
  }
  
  getMenuData() {
    // Try to get menu data from existing Shopify menu
    const existingMenu = document.querySelector('.menu-drawer__menu');
    if (!existingMenu) {
      return this.createMenuWithCollections();
    }
    
    const menuData = [];
    const topLevelItems = existingMenu.querySelectorAll(':scope > li');
    
    topLevelItems.forEach(item => {
      const link = item.querySelector('a, summary');
      if (!link) return;
      
      const menuItem = {
        title: link.textContent.trim(),
        url: link.href || '#',
        links: []
      };
      
      const submenu = item.querySelector('.menu-drawer__submenu');
      if (submenu) {
        const subLinks = submenu.querySelectorAll('.menu-drawer__menu > li > a');
        subLinks.forEach(subLink => {
          menuItem.links.push({
            title: subLink.textContent.trim(),
            url: subLink.href || '#'
          });
        });
      } else {
        const collections = this.getCollections();
        if (collections.length > 0) {
          menuItem.links = collections;
        }
      }
      
      menuData.push(menuItem);
    });
    
    return menuData;
  }
  
  createMenuWithCollections() {
    const collections = this.getCollections();
    
    return [
      {
        title: 'Shop',
        url: '/collections',
        links: collections
      },
      {
        title: 'About',
        url: '/pages/about',
        links: []
      },
      {
        title: 'Contact',
        url: '/pages/contact',
        links: []
      }
    ];
  }
  
  getCollections() {
    const collections = [];
    
    if (window.collections) {
      Object.values(window.collections).forEach(collection => {
        if (collection.handle !== 'frontpage') {
          collections.push({
            title: collection.title,
            url: `/collections/${collection.handle}`
          });
        }
      });
    } else {
      collections.push(
        { title: 'All Products', url: '/collections/all' },
        { title: 'New Arrivals', url: '/collections/new-arrivals' },
        { title: 'Best Sellers', url: '/collections/best-sellers' },
        { title: 'Featured', url: '/collections/featured' },
        { title: 'Sale', url: '/collections/sale' }
      );
    }
    
    collections.sort((a, b) => a.title.localeCompare(b.title));
    return collections;
  }
  
  bindEvents() {
    // Single centralized click handler for the entire menu
    this.menuDrawer.addEventListener('click', (e) => {
      this.handleMenuClick(e);
    });
    
    // Hamburger button
    if (this.hamburgerButton) {
      const newButton = this.hamburgerButton.cloneNode(true);
      this.hamburgerButton.parentNode.replaceChild(newButton, this.hamburgerButton);
      this.hamburgerButton = newButton;
      
      this.hamburgerButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }
    
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }
    
    // Overlay
    if (this.menuOverlay) {
      this.menuOverlay.addEventListener('click', () => this.close());
    }
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        if (this.activeSubmenu) {
          this.closeSubmenu();
        } else {
          this.close();
        }
      }
    });
  }
  
  handleMenuClick(e) {
    const target = e.target.closest('button, a');
    if (!target) return;
    
    // Handle main menu button clicks
    if (target.classList.contains('sachraga-menu-link') && target.dataset.menuIndex) {
      e.preventDefault();
      e.stopPropagation();
      const menuIndex = parseInt(target.dataset.menuIndex);
      console.log('Main menu button clicked:', target.dataset.menuTitle);
      this.toggleSubmenu(menuIndex);
      return;
    }
    
    // Handle back button clicks
    if (target.classList.contains('sachraga-submenu-back')) {
      e.preventDefault();
      e.stopPropagation();
      console.log('Back button clicked');
      this.closeSubmenu();
      return;
    }
    
    // Handle submenu link clicks (allow default navigation)
    if (target.classList.contains('sachraga-submenu-link')) {
      console.log('Submenu link clicked:', target.textContent);
      // Don't prevent default - allow navigation
      return;
    }
  }
  
  toggleSubmenu(menuIndex) {
    const submenu = this.submenuContainer.querySelector(`[data-menu-index="${menuIndex}"]`);
    if (!submenu) return;
    
    if (this.activeSubmenu === submenu) {
      // Same submenu clicked - close it
      this.closeSubmenu();
    } else {
      // Different submenu or no submenu active - open it
      this.openSubmenu(submenu);
    }
  }
  
  openSubmenu(submenu) {
    if (!submenu) return;
    
    // Close any active submenu first
    if (this.activeSubmenu) {
      this.activeSubmenu.style.transform = 'translateX(100%)';
    }
    
    // Open new submenu
    submenu.style.transform = 'translateX(0)';
    this.activeSubmenu = submenu;
    
    console.log('Opened submenu:', submenu.dataset.parentTitle);
    
    // Hide main menu on mobile
    if (this.isMobile && this.menuNav) {
      this.menuNav.style.transform = 'translateX(-100%)';
    }
  }
  
  closeSubmenu() {
    if (!this.activeSubmenu) return;
    
    console.log('Closed submenu:', this.activeSubmenu.dataset.parentTitle);
    
    // Close submenu
    this.activeSubmenu.style.transform = 'translateX(100%)';
    this.activeSubmenu = null;
    
    // Show main menu on mobile
    if (this.isMobile && this.menuNav) {
      this.menuNav.style.transform = 'translateX(0)';
    }
  }
  
  open() {
    if (!this.menuDrawer) return;
    
    this.isOpen = true;
    this.menuDrawer.classList.add('menu-open');
    document.body.classList.add('sachraga-menu-open');
    
    this.closeButton.focus();
  }
  
  close() {
    if (!this.menuDrawer) return;
    
    this.isOpen = false;
    this.menuDrawer.classList.remove('menu-open');
    document.body.classList.remove('sachraga-menu-open');
    
    // Close any active submenu
    this.closeSubmenu();
    
    if (this.hamburgerButton) {
      this.hamburgerButton.focus();
    }
  }
}

// Initialize the fixed menu
document.addEventListener('DOMContentLoaded', () => {
  new SachragaMenuFixed();
});
